/**
 * Packing slip review page (Phase 4A).
 *
 * Admin-only operational console for AI packing-slip processing. Two tabs:
 *   - Needs Review: pages where status is in (manual_review, matched_no_parts,
 *     failed) AND not dismissed. Default tab.
 *   - Recent Successful Receipts: status=matched, parts_received_count>0,
 *     created within the last 7 days. Read-only view for confidence.
 *
 * Polls the list every 60s. Click a row to open the detail drawer.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchPackingSlipPages } from '../api/purchasing';
import PackingSlipDetailDrawer from '../components/purchasing/PackingSlipDetailDrawer';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_PILLS = {
  matched: { label: 'Matched', cls: 'bg-green-100 text-green-800' },
  matched_no_parts: { label: 'No Parts', cls: 'bg-amber-100 text-amber-800' },
  manual_review: { label: 'Review', cls: 'bg-amber-100 text-amber-800' },
  unmatched: { label: 'Unmatched', cls: 'bg-gray-100 text-gray-700' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-800' },
  pending: { label: 'Pending', cls: 'bg-blue-100 text-blue-800' },
  processing: { label: 'Processing', cls: 'bg-blue-100 text-blue-800' },
};

function StatusPill({ status }) {
  const cfg = STATUS_PILLS[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const now = Date.now();
    const diffMs = now - dt.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return d;
  }
}

function fmtConfidence(c) {
  if (c === null || c === undefined) return '';
  const n = parseFloat(c);
  if (Number.isNaN(n)) return '';
  return `${Math.round(n * 100)}%`;
}

function truncate(s, n = 100) {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

// ---------------------------------------------------------------------------
// Card row
// ---------------------------------------------------------------------------

function PageCard({ page, onClick }) {
  const isReview = ['manual_review', 'matched_no_parts', 'failed'].includes(page.status);
  const borderColor = isReview ? 'border-amber-200 hover:border-amber-300' : 'border-gray-200 hover:border-gray-300';

  return (
    <button
      onClick={() => onClick(page.id)}
      className={`w-full text-left bg-white rounded-lg border ${borderColor} hover:shadow-sm transition-all`}
    >
      <div className="px-4 py-3">
        {/* Header row */}
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <StatusPill status={page.status} />
          {page.is_dismissed && (
            <span className="px-1.5 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded">
              Dismissed
            </span>
          )}
          {page.matched_po_number && (
            <span className="text-sm font-mono font-semibold text-gray-700">
              {page.matched_po_number}
            </span>
          )}
          {page.vendor_name && (
            <span className="text-sm text-gray-600">— {page.vendor_name}</span>
          )}
          {page.job_number && (
            <span className="text-xs text-gray-400 ml-1">
              Job #{page.job_number}
              {page.customer_name && ` · ${page.customer_name}`}
            </span>
          )}
          <span className="text-xs text-gray-400 ml-auto shrink-0">
            {fmtDate(page.created_at)}
          </span>
        </div>

        {/* Detail row */}
        <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
          {page.extracted_po_number && (
            <span>
              Slip PO: <span className="font-mono text-gray-700">{page.extracted_po_number}</span>
            </span>
          )}
          {page.ai_vendor_identified && (
            <span>
              AI vendor: <span className="text-gray-700">{page.ai_vendor_identified}</span>
            </span>
          )}
          {page.ai_confidence !== null && page.ai_confidence !== undefined && (
            <span>
              Conf: <span className="text-gray-700">{fmtConfidence(page.ai_confidence)}</span>
            </span>
          )}
          {page.parts_received_count > 0 && (
            <span>
              Received: <span className="text-gray-700 font-mono">{page.parts_received_count}</span>
            </span>
          )}
        </div>

        {/* Error / reasoning */}
        {page.error_message && (
          <p className="mt-1.5 text-xs text-red-700 line-clamp-2">
            {truncate(page.error_message, 200)}
          </p>
        )}
        {!page.error_message && page.ai_reasoning && (
          <p className="mt-1.5 text-xs text-gray-500 line-clamp-2 italic">
            {truncate(page.ai_reasoning, 200)}
          </p>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'needs_review', label: 'Needs Review' },
  { key: 'recent_success', label: 'Recent Successful Receipts' },
];

export default function PackingSlipReviewPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('needs_review');
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const result = await fetchPackingSlipPages({ queue: activeTab });
      setItems(result.items);
      setCount(result.count);
      setError('');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError('Admin access required.');
      } else {
        setError(err?.response?.data?.error || err?.message || 'Failed to load pages.');
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(loadData, 60_000);
    return () => clearInterval(interval);
  }, [loadData]);

  // After drawer action (dismiss / reprocess), refresh list
  const handleDrawerChange = useCallback(() => {
    loadData();
  }, [loadData]);

  // Admin gate (kept in addition to backend enforcement so the route doesn't flash content)
  if (user && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const heading = useMemo(() => {
    if (activeTab === 'needs_review') return 'Receiving Review';
    return 'Recent Successful Receipts';
  }, [activeTab]);

  const subheading = useMemo(() => {
    if (activeTab === 'needs_review') {
      return 'Packing slip pages that need operator attention. Reprocess to retry, or dismiss once triaged.';
    }
    return 'Successfully matched packing slips from the last 7 days.';
  }, [activeTab]);

  return (
    <div className="max-w-5xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{heading}</h1>
          <p className="text-sm text-gray-500 mt-1">{subheading}</p>
        </div>
        <div className="flex items-center gap-3">
          {!loading && count > 0 && activeTab === 'needs_review' && (
            <span className="px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold">
              {count} pending
            </span>
          )}
          <button
            onClick={() => { setLoading(true); loadData(); }}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <div className="text-4xl mb-3">{activeTab === 'needs_review' ? '✅' : '📦'}</div>
          <h2 className="text-lg font-semibold text-gray-700">
            {activeTab === 'needs_review' ? 'All caught up' : 'No recent receipts'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {activeTab === 'needs_review'
              ? 'No packing slip pages need attention right now.'
              : 'No successfully-matched packing slips in the last 7 days.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((page) => (
            <PageCard key={page.id} page={page} onClick={setSelectedId} />
          ))}
        </div>
      )}

      {/* Detail drawer */}
      <PackingSlipDetailDrawer
        pageId={selectedId}
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
        onChange={handleDrawerChange}
      />
    </div>
  );
}
